import { useCallback, useState } from 'react';

import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';

import {
  getVerificationSetupProgress,
  initializeVerificationSetup,
  completeVerificationStep,
} from '@/apis/experience/verification-setup';

import type {
  VerificationProgress,
  VerificationStepType,
  StepValidationData,
  SetupProgressResponse,
} from '../types';

const useSetupProgress = () => {
  const [progress, setProgress] = useState<VerificationProgress | null>(null);
  const [currentStep, setCurrentStep] = useState<VerificationStepType | null>(null);
  const [nextStep, setNextStep] = useState<VerificationStepType | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [setupId, setSetupId] = useState<string | null>(null);

  const asyncGetProgress = useApi(getVerificationSetupProgress);
  const asyncInitializeSetup = useApi(initializeVerificationSetup);
  const asyncCompleteStep = useApi(completeVerificationStep);
  const handleError = useErrorHandler();

  // Initialize the setup progress
  const initializeProgress = useCallback(async () => {
    try {
      const [initError, initResult] = await asyncInitializeSetup();
      
      if (initError) {
        await handleError(initError);
        return;
      }

      if (initResult) {
        setSetupId(initResult.setupId);
        setProgress(initResult.progress);
        setCurrentStep(initResult.nextStep);
        setNextStep(initResult.nextStep);
        
        // Calculate completion percentage
        const completedSteps = [
          initResult.progress.emailVerified,
          initResult.progress.whatsappVerified,
          initResult.progress.totpSetup,
          initResult.progress.deviceRegistered,
        ].filter(Boolean).length;
        
        setCompletionPercentage((completedSteps / 4) * 100);
      }
    } catch (error) {
      await handleError(error);
    }
  }, [asyncInitializeSetup, handleError]);

  // Refresh progress from server
  const refreshProgress = useCallback(async () => {
    try {
      const [progressError, progressResult] = await asyncGetProgress();
      
      if (progressError) {
        await handleError(progressError);
        return;
      }

      if (progressResult) {
        setProgress(progressResult.progress);
        setCurrentStep(progressResult.currentStep);
        setNextStep(progressResult.nextStep);
        setCompletionPercentage(progressResult.completionPercentage);
      }
    } catch (error) {
      await handleError(error);
    }
  }, [asyncGetProgress, handleError]);

  // Complete a specific step
  const completeStep = useCallback(async (
    stepType: VerificationStepType,
    validationData: StepValidationData
  ) => {
    try {
      const [completeError, completeResult] = await asyncCompleteStep(stepType, validationData);
      
      if (completeError) {
        await handleError(completeError);
        return;
      }

      if (completeResult) {
        setProgress(completeResult.progress);
        setNextStep(completeResult.nextStep);
        setCurrentStep(completeResult.nextStep);
        
        // Update completion percentage
        const completedSteps = [
          completeResult.progress.emailVerified,
          completeResult.progress.whatsappVerified,
          completeResult.progress.totpSetup,
          completeResult.progress.deviceRegistered,
        ].filter(Boolean).length;
        
        setCompletionPercentage((completedSteps / 4) * 100);

        return completeResult;
      }
    } catch (error) {
      await handleError(error);
      throw error;
    }
  }, [asyncCompleteStep, handleError]);

  // Check if setup is completed
  const isSetupCompleted = progress && 
    progress.emailVerified && 
    progress.whatsappVerified && 
    progress.totpSetup && 
    progress.deviceRegistered;

  // Get the status of a specific step
  const getStepStatus = useCallback((stepType: VerificationStepType): 'pending' | 'in-progress' | 'completed' => {
    if (!progress) return 'pending';

    const stepCompleted = {
      email: progress.emailVerified,
      whatsapp: progress.whatsappVerified,
      totp: progress.totpSetup,
      device: progress.deviceRegistered,
    }[stepType];

    if (stepCompleted) return 'completed';
    if (currentStep === stepType) return 'in-progress';
    return 'pending';
  }, [progress, currentStep]);

  // Check if a step is accessible (previous steps must be completed)
  const isStepAccessible = useCallback((stepType: VerificationStepType): boolean => {
    if (!progress) return stepType === 'email';

    switch (stepType) {
      case 'email':
        return true;
      case 'whatsapp':
        return progress.emailVerified;
      case 'totp':
        return progress.emailVerified && progress.whatsappVerified;
      case 'device':
        return progress.emailVerified && progress.whatsappVerified && progress.totpSetup;
      default:
        return false;
    }
  }, [progress]);

  return {
    // State
    progress,
    currentStep,
    nextStep,
    completionPercentage,
    setupId,
    isSetupCompleted,

    // Actions
    initializeProgress,
    refreshProgress,
    completeStep,
    
    // Utilities
    getStepStatus,
    isStepAccessible,
  };
};

export default useSetupProgress;